













































































import Vue from "vue";
import DateInput from "@/components/DateInput.vue";
import ApiSearch from "@/components/apiSearch/models/ApiSearch";
import { FilteringModel } from "@/services/filtering";
import { StoreInOutPreAccLookups } from "../models/StoreInOutPreAccounting";

interface Data {
  periodId: number | null;
  ready: boolean;
  defaultsApplied: boolean;
}

interface Methods {
  ensureFilters(): void;
  applyDefaultSelections(): void;
  onPeriodChange(): void;
  syncPeriodFromDates(): void;
  setFlag(name: string, value: boolean): void;
  getFlag(name: string, fallback: boolean): boolean;
}

interface Computed {
  filters: any;
  accStatusOptions: any[];
  periodOptions: any[];
  showStoreIn: boolean;
  showGStoreIn: boolean;
  showStoreOut: boolean;
  showKek: boolean;
}

interface Props {
  apiSearch: ApiSearch;
  lookups: StoreInOutPreAccLookups | null;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    DateInput,
  },

  props: {
    apiSearch: { type: Object as () => ApiSearch },
    lookups: { type: Object as () => StoreInOutPreAccLookups | null, default: null },
  },

  data() {
    return {
      periodId: null,
      ready: false,
      defaultsApplied: false,
    };
  },

  created() {
    this.ensureFilters();
    this.applyDefaultSelections();
    this.syncPeriodFromDates();
  },

  watch: {
    lookups() {
      this.applyDefaultSelections();
      this.syncPeriodFromDates();
    },
  },

  methods: {
    ensureFilters() {
      const filter = (this.$props.apiSearch as any).filter as FilteringModel;
      if (!filter) return;

      // A default-értékek beírása NE váltson ki extra betöltést: az ApiSearchPage
      // deep-watchere a stopFilterLoad kapun át egyszer átugorja a reload-ot
      // (az első lekérdezés a mounted-ből amúgy is elindul, már ezekkel az értékekkel)
      (this.$props.apiSearch as any).stopFilterLoad = true;

      const template = new FilteringModel()
        .value("startDate")
        .value("endDate")
        .multiSelect("selectedAccStatuses", [], false)
        .value("showStoreIn")
        .value("showGStoreIn")
        .value("showStoreOut")
        .value("showKek");

      let statusesCreated = false;
      Object.entries(template.filters).forEach(([key, value]) => {
        if (!filter.filters[key]) {
          Vue.set(filter.filters, key, value);
          if (key === "selectedAccStatuses") statusesCreated = true;
        }
      });

      // persistFilters: ha a státusz-pipák kulcsa már a visszatöltött modellből jött, a mentett
      // kijelölés marad, a K2D-alapállapot nem íródik rá (a folyószámla panel mintája)
      if (!statusesCreated) this.defaultsApplied = true;

      // K2D-alapállapot (w_popup_bank_cash, a checkbox-definíciók design-time
      // állapota, futásidejű felülírás nincs): Gyári betárolás BE (cbx_storein
      // checked=true, :4074), Egyéb betárolás KI (cbx_gstorein-en nincs checked,
      // :4111), Kitárolás BE (cbx_storeout checked=true, :4147), KÉK KI (:4337)
      if (this.filters.showStoreIn.value == null) this.filters.showStoreIn.value = "1";
      if (this.filters.showGStoreIn.value == null) this.filters.showGStoreIn.value = "0";
      if (this.filters.showStoreOut.value == null) this.filters.showStoreOut.value = "1";
      if (this.filters.showKek.value == null) this.filters.showKek.value = "0";

      this.ready = true;
    },

    // K2D-hű alapállapot: az időszak ÜRESEN indul (a K2D em_tol/em_ig sincs
    // inicializálva), a kontír státuszból viszont MINDEN pipálva
    // (dw_status retrieveend: kivalaszt = '+')
    applyDefaultSelections() {
      if (this.defaultsApplied) return;
      if (!this.$props.lookups) return;
      if (!this.filters.selectedAccStatuses) return;

      // Az összes státusz kipipálása a szerveren ugyanazt a találati halmazt adja,
      // mint a szűretlen állapot — a beírása ne indítson felesleges újratöltést
      (this.$props.apiSearch as any).stopFilterLoad = true;
      this.filters.selectedAccStatuses.selectedOptions = this.accStatusOptions.map(
        (x: any) => x.value
      );

      this.defaultsApplied = true;
    },

    onPeriodChange() {
      const period = (this.$props.lookups?.periods ?? []).find((x: any) => x.id === this.periodId);
      if (!period) return;
      this.filters.startDate.value = period.fromDate;
      this.filters.endDate.value = period.toDate;
    },

    // Visszatöltött (persistFilters) időszak-dátumoknál az időszak-választó is a megfelelő
    // periódusra álljon
    syncPeriodFromDates() {
      const from = this.filters.startDate?.value;
      const to = this.filters.endDate?.value;
      if (!from || !to) return;
      const period = (this.$props.lookups?.periods ?? []).find((x: any) => x.fromDate === from && x.toDate === to);
      if (period) this.periodId = period.id;
    },

    setFlag(name: string, value: boolean) {
      const target = this.filters[name];
      if (target) target.value = value ? "1" : "0";
    },

    getFlag(name: string, fallback: boolean): boolean {
      const raw = this.filters[name]?.value;
      if (raw == null) return fallback;
      return raw !== "0";
    },
  },

  computed: {
    filters(): any {
      return (this.$props.apiSearch as any).filter?.filters ?? {};
    },

    accStatusOptions(): any[] {
      return (this.$props.lookups?.accStatuses ?? []).map((x: any) => ({
        text: x.name,
        value: String(x.id),
      }));
    },

    periodOptions(): any[] {
      return (this.$props.lookups?.periods ?? []).map((x: any) => ({
        text: x.nicName,
        value: x.id,
      }));
    },

    // A négy bizonylattípus-checkbox a szűrőmodellben "1"/"0"-ként utazik;
    // a KÉK és a három tároló-típus kölcsönösen kizárja egymást (K2D cbx_kek)
    showStoreIn: {
      get(): boolean { return this.getFlag("showStoreIn", true); },
      set(value: boolean) {
        this.setFlag("showStoreIn", value);
        if (value) this.setFlag("showKek", false);
      },
    },
    showGStoreIn: {
      get(): boolean { return this.getFlag("showGStoreIn", false); },
      set(value: boolean) {
        this.setFlag("showGStoreIn", value);
        if (value) this.setFlag("showKek", false);
      },
    },
    showStoreOut: {
      get(): boolean { return this.getFlag("showStoreOut", true); },
      set(value: boolean) {
        this.setFlag("showStoreOut", value);
        if (value) this.setFlag("showKek", false);
      },
    },
    showKek: {
      get(): boolean { return this.getFlag("showKek", false); },
      set(value: boolean) {
        this.setFlag("showKek", value);
        if (value) {
          this.setFlag("showStoreIn", false);
          this.setFlag("showGStoreIn", false);
          this.setFlag("showStoreOut", false);
        }
      },
    },
  },
});
